import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  LockClosedIcon,
  EnvelopeIcon,
  UserIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import {
  useUser,
  useDeleteUser,
  useArchiveUser,
  useOnboarding,
} from "../../features/users/hooks";
import { useForcePasswordReset } from "../../features/users/hooks/useForcePasswordReset";
import { formatDateTime } from "../../utils/dateUtils";

/**
 * Page to view detailed information about a user
 */
const UserDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isLoading, isError, error } = useUser(id);
  const { deleteUser, isLoading: isDeleting } = useDeleteUser();
  const { archiveUser, isArchiving } = useArchiveUser();
  const { sendOnboardingEmail, isSending } = useOnboarding();
  const { toggleForcePasswordReset } = useForcePasswordReset();

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // State for archive confirmation dialog
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  // State for onboarding confirmation dialog
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  // State for reset password confirmation dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Handle edit user button click
  const handleEditUser = () => {
    if (id) {
      navigate(`/users/edit/${id}`);
    }
  };

  // Handle reset password button click
  const handleResetPassword = () => {
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordDialogClose = () => {
    setResetPasswordDialogOpen(false);
  };

  const handleResetPasswordConfirm = async () => {
    if (id) {
      const result = await toggleForcePasswordReset(id, true);
      if (result.success) {
        setNotification({
          open: true,
          message: "Password reset flag set successfully",
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: `Failed to set password reset flag: ${result.error}`,
          severity: "error",
        });
      }
    }
    handleResetPasswordDialogClose();
  };

  // Delete user dialog handlers
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (id) {
      const result = await deleteUser(id);

      if (result.success) {
        setNotification({
          open: true,
          message: "User deleted successfully",
          severity: "success",
        });

        // Navigate back to users list after a short delay
        setTimeout(() => {
          navigate("/users");
        }, 1500);
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to delete user",
          severity: "error",
        });
      }
    }

    handleDeleteDialogClose();
  };

  // Archive user dialog handlers
  const handleArchiveDialogOpen = () => {
    setArchiveDialogOpen(true);
  };

  const handleArchiveDialogClose = () => {
    setArchiveDialogOpen(false);
  };

  const handleArchiveConfirm = async () => {
    if (id) {
      const result = await archiveUser(id);

      if (result.success) {
        setNotification({
          open: true,
          message: "User archived successfully",
          severity: "success",
        });

        // Navigate back to users list after a short delay
        setTimeout(() => {
          navigate("/users");
        }, 1500);
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to archive user",
          severity: "error",
        });
      }
    }

    handleArchiveDialogClose();
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Onboarding dialog handlers
  const handleOnboardingDialogOpen = () => {
    setOnboardingDialogOpen(true);
  };

  const handleOnboardingDialogClose = () => {
    setOnboardingDialogOpen(false);
  };

  const handleOnboardingConfirm = async () => {
    if (id) {
      const result = await sendOnboardingEmail(id);

      if (result.success) {
        setNotification({
          open: true,
          message:
            user?.accountStatus === "CREATED"
              ? "Onboarding email sent successfully"
              : "Onboarding email resent successfully",
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to send onboarding email",
          severity: "error",
        });
      }
    }

    handleOnboardingDialogClose();
  };

  // Get appropriate color for role badge
  const getRoleBadgeClasses = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      case "MANAGER":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "SUPERVISOR":
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400";
      case "SERVER":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  // Get appropriate color for status badge
  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "SUSPENDED":
      case "LOCKED":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "UNVERIFIED":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "CREATED":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "ONBOARDING":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400";
      case "ARCHIVED":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200">
            {error?.message || "Failed to load user details"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li>
            <div className="flex items-center">
              <button
                onClick={() => navigate("/")}
                className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary"
              >
                Dashboard
              </button>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => navigate("/users")}
                className="ml-1 text-gray-700 hover:text-primary md:ml-2 dark:text-gray-300 dark:hover:text-primary"
              >
                Users
              </button>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-gray-500 md:ml-2 dark:text-gray-400">
                {user.firstName} {user.lastName}
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-500 dark:text-white">
          User Details
        </h1>

        <div className="flex gap-2">
          {(user.accountStatus === "CREATED" ||
            user.accountStatus === "ONBOARDING") && (
            <button
              onClick={handleOnboardingDialogOpen}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <EnvelopeIcon className="mr-2 h-4 w-4" />
              {user.accountStatus === "CREATED"
                ? "Send Onboarding Email"
                : "Resend Onboarding Email"}
            </button>
          )}
          <button
            onClick={handleEditUser}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-darkmode-800 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit
          </button>
          <button
            onClick={handleResetPassword}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-darkmode-800 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <LockClosedIcon className="mr-2 h-4 w-4" />
            Reset Password
          </button>
          {user.accountStatus !== "ARCHIVED" && (
            <button
              onClick={handleArchiveDialogOpen}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-darkmode-800 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <ArchiveBoxIcon className="mr-2 h-4 w-4" />
              Archive
            </button>
          )}
          <button
            onClick={handleDeleteDialogOpen}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-500 dark:text-white flex items-center mb-4">
              <UserIcon className="mr-2 h-5 w-5" />
              Basic Information
            </h2>
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Name
                </p>
                <p className="mt-1 text-base text-gray-500 dark:text-white">
                  {user.firstName} {user.lastName}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                  <EnvelopeIcon className="mr-1 h-4 w-4" />
                  Email
                </p>
                <p className="mt-1 text-base text-gray-500 dark:text-white">
                  {user.email}
                  {user.pendingEmail && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                      Pending: {user.pendingEmail}
                    </span>
                  )}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Role
                </p>
                <p className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClasses(user.role)}`}
                  >
                    {user.role}
                  </span>
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </p>
                <p className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(user.accountStatus)}`}
                  >
                    {user.accountStatus}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Account Security */}
          <div>
            <h2 className="text-lg font-medium text-gray-500 dark:text-white flex items-center mb-4">
              <ShieldCheckIcon className="mr-2 h-5 w-5" />
              Account Security
            </h2>
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  MFA Enabled
                </p>
                <p className="mt-1">
                  {user.mfaEnabled ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                      Enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      Disabled
                    </span>
                  )}
                </p>
              </div>

              {user.mfaEnabled && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    MFA Type
                  </p>
                  <p className="mt-1 text-base text-gray-500 dark:text-white">
                    {user.mfaType || "Not specified"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Activity Timeline */}
          <div>
            <h2 className="text-lg font-medium text-gray-500 dark:text-white flex items-center mb-4">
              <CalendarIcon className="mr-2 h-5 w-5" />
              Activity Timeline
            </h2>
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Created At
                </p>
                <p className="mt-1 text-base text-gray-500 dark:text-white">
                  {formatDateTime(user.createdAt)}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Last Updated
                </p>
                <p className="mt-1 text-base text-gray-500 dark:text-white">
                  {formatDateTime(user.updatedAt)}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Last Login
                </p>
                <p className="mt-1 text-base text-gray-500 dark:text-white">
                  {user.lastLoginAt
                    ? formatDateTime(user.lastLoginAt)
                    : "Never logged in"}
                </p>
              </div>

              {(user.accountStatus === "ONBOARDING" ||
                user.accountStatus === "ACTIVE") &&
                user.onboardingEmailSentAt && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Onboarding Email Sent
                    </p>
                    <p className="mt-1 text-base text-gray-500 dark:text-white">
                      {formatDateTime(user.onboardingEmailSentAt)}
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleDeleteDialogClose}
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      Delete User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete {user.firstName}{" "}
                        {user.lastName}? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "Delete"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteDialogClose}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Confirmation Dialog */}
      {onboardingDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleOnboardingDialogClose}
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <EnvelopeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      {user.accountStatus === "CREATED"
                        ? "Send Onboarding Email"
                        : "Resend Onboarding Email"}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {user.accountStatus === "CREATED"
                          ? `Send an onboarding email to ${user.firstName} ${user.lastName}? They will receive instructions to set up their account.`
                          : `Resend the onboarding email to ${user.firstName} ${user.lastName}? They will receive a new link to complete their account setup.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleOnboardingConfirm}
                  disabled={isSending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "Send Email"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleOnboardingDialogClose}
                  disabled={isSending}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Dialog */}
      {resetPasswordDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleResetPasswordDialogClose}
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <LockClosedIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      Force Password Reset
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to force {user.firstName}{" "}
                        {user.lastName} to reset their password on next login?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleResetPasswordConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Force Reset
                </button>
                <button
                  type="button"
                  onClick={handleResetPasswordDialogClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Dialog */}
      {archiveDialogOpen && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-darkmode-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <ArchiveBoxIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      Archive User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to archive {user.firstName}{" "}
                        {user.lastName}? The user will no longer be able to
                        access the system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleArchiveConfirm}
                  disabled={isArchiving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isArchiving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "Archive"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleArchiveDialogClose}
                  disabled={isArchiving}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.open && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`
            max-w-xl w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden
            ${notification.severity === "success" ? "bg-green-50 dark:bg-green-900/20" : ""}
            ${notification.severity === "error" ? "bg-red-50 dark:bg-red-900/20" : ""}
            ${notification.severity === "warning" ? "bg-yellow-50 dark:bg-yellow-900/20" : ""}
            ${notification.severity === "info" ? "bg-blue-50 dark:bg-blue-900/20" : ""}
          `}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.severity === "success" && (
                    <svg
                      className="h-6 w-6 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                  {notification.severity === "error" && (
                    <svg
                      className="h-6 w-6 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1 pt-0.5">
                  <p
                    className={`
                    text-sm font-medium
                    ${notification.severity === "success" ? "text-green-800 dark:text-green-200" : ""}
                    ${notification.severity === "error" ? "text-red-800 dark:text-red-200" : ""}
                    ${notification.severity === "warning" ? "text-yellow-800 dark:text-yellow-200" : ""}
                    ${notification.severity === "info" ? "text-blue-800 dark:text-blue-200" : ""}
                  `}
                  >
                    {notification.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    onClick={handleNotificationClose}
                    className={`
                      inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${notification.severity === "success" ? "text-green-500 hover:text-green-600 focus:ring-green-500" : ""}
                      ${notification.severity === "error" ? "text-red-500 hover:text-red-600 focus:ring-red-500" : ""}
                      ${notification.severity === "warning" ? "text-yellow-500 hover:text-yellow-600 focus:ring-yellow-500" : ""}
                      ${notification.severity === "info" ? "text-blue-500 hover:text-blue-600 focus:ring-blue-500" : ""}
                    `}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailsPage;
